/**
 * TypeScript mirrors of the server-side C# event classes Portal-SPA cares about.
 * Discriminator field `$type` holds the C# class name (matches .NET JsonPolymorphic).
 * Use @className-decorated token classes with EventBusService.on() for routing.
 *
 * Ported from Chroniq/Mobile-Client's events.model.ts, trimmed to the domains Portal-SPA
 * actually has (Employee/Shift/ShiftTemplate/Absence/TimeEntry) — no chat here, Portal-SPA
 * is Chef/Admin-only and has no chat feature (see docs/todo/portal-spa-basisfunktionen.md).
 */

import { className } from '@core/decorators/class-name.decorator';

import { type InviteStatus } from './employee.model';

// ─── Base ─────────────────────────────────────────────────────────────────────

export interface BaseEvent {
  readonly $type: string; // JSON discriminator — matches C# class name
  eventId: string; // GUID
  tenantId: string;
  occurredAt: string; // ISO datetime
}

// ─── Time Tracking Events ─────────────────────────────────────────────────────

/**
 * Match Chroniq.Shared.Events.TimeTracking.* exactly (camelCase of the C# record fields).
 * `employeeId` is the employee's Auth0 sub (Employee.userId), NOT the internal Employee.id,
 * because the time-tracking commands are issued with the sub. `timeEntryId` is the entry's id.
 */
export interface TimeEntryStartedEvent extends BaseEvent {
  readonly $type: 'TimeEntryStartedEvent';
  timeEntryId: string;
  employeeId: string; // Auth0 sub
  startTime: string; // ISO datetime with offset
}

export interface TimeEntryStoppedEvent extends BaseEvent {
  readonly $type: 'TimeEntryStoppedEvent';
  timeEntryId: string;
  employeeId: string; // Auth0 sub
  startTime: string;
  endTime: string;
  totalWorkedMinutes: number;
  totalBreakMinutes: number;
}

export interface BreakStartedEvent extends BaseEvent {
  readonly $type: 'BreakStartedEvent';
  timeEntryId: string;
  breakId: string;
  employeeId: string; // Auth0 sub
  breakStartTime: string;
}

export interface BreakEndedEvent extends BaseEvent {
  readonly $type: 'BreakEndedEvent';
  timeEntryId: string;
  breakId: string;
  employeeId: string; // Auth0 sub
  breakStartTime: string;
  breakEndTime: string;
  durationMinutes: number;
}

/** A Chef/Admin corrected start, end or break of an entry (CorrectTimeEntry, SetBreakMinutes). */
export interface TimeEntryCorrectedEvent extends BaseEvent {
  readonly $type: 'TimeEntryCorrectedEvent';
  timeEntryId: string;
  employeeId: string; // Auth0 sub
  newStartTime: string;
  newEndTime: string;
  totalWorkedMinutes: number;
}

/** A Chef/Admin entered a completed entry after the fact. */
export interface TimeEntryCreatedEvent extends BaseEvent {
  readonly $type: 'TimeEntryCreatedEvent';
  timeEntryId: string;
  employeeId: string; // Auth0 sub
  startTime: string;
  endTime: string;
  totalWorkedMinutes: number;
}

/** A Chef/Admin cancelled an entry. */
export interface TimeEntryDeletedEvent extends BaseEvent {
  readonly $type: 'TimeEntryDeletedEvent';
  timeEntryId: string;
  employeeId: string; // Auth0 sub
}

// ─── Employee Events ──────────────────────────────────────────────────────────

/**
 * Matches Chroniq.Shared.Events.Employee.EmployeeUpdatedEvent exactly — a straight passthrough
 * of the PATCH command's fields (EmployeeProcessor.HandleUpdate). This is NOT a
 * sparse/omitted-when-unset DTO: every field the PATCH didn't touch arrives as an explicit
 * `null` on the wire (ServiceBusHelper's JSON options have no DefaultIgnoreCondition), never as
 * an omitted key — hence `| null` here, not `?:`. AhvNumber/Iban are never included at all
 * (sensitive fields are excluded from events server-side). IsActive/InviteStatus are ALWAYS
 * `null` for this event (only other commands ever set them) — do not read them from here.
 */
export interface EmployeeUpdatedEvent extends BaseEvent {
  readonly $type: 'EmployeeUpdatedEvent';
  employeeId: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  position: string | null;
  department: string | null;
  isActive: boolean | null;
  inviteStatus: InviteStatus | null;
}

export interface EmployeeAnonymisedEvent extends BaseEvent {
  readonly $type: 'EmployeeAnonymisedEvent';
  employeeId: string;
}

// ─── Shift Events ─────────────────────────────────────────────────────────────

export interface ShiftCreatedEvent extends BaseEvent {
  readonly $type: 'ShiftCreatedEvent';
  shiftId: string;
  employeeId: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm:ss
  endTime: string;
  title?: string | null;
}

export interface ShiftUpdatedEvent extends BaseEvent {
  readonly $type: 'ShiftUpdatedEvent';
  shiftId: string;
  employeeId: string;
  date: string;
  startTime: string;
  endTime: string;
  title?: string | null;
}

export interface ShiftDeletedEvent extends BaseEvent {
  readonly $type: 'ShiftDeletedEvent';
  shiftId: string;
  employeeId: string;
  date: string;
}

// ─── Shift Template Events ─────────────────────────────────────────────────────

export interface ShiftTemplateCreatedEvent extends BaseEvent {
  readonly $type: 'ShiftTemplateCreatedEvent';
  templateId: string;
}
export interface ShiftTemplateUpdatedEvent extends BaseEvent {
  readonly $type: 'ShiftTemplateUpdatedEvent';
  templateId: string;
}
export interface ShiftTemplateDeletedEvent extends BaseEvent {
  readonly $type: 'ShiftTemplateDeletedEvent';
  templateId: string;
}

// ─── Absence Events ─────────────────────────────────────────────────────────────

export interface AbsenceRequestedEvent extends BaseEvent {
  readonly $type: 'AbsenceRequestedEvent';
  absenceId: string;
  employeeId: string;
}
export interface AbsenceApprovedEvent extends BaseEvent {
  readonly $type: 'AbsenceApprovedEvent';
  absenceId: string;
  employeeId: string;
}
export interface AbsenceRejectedEvent extends BaseEvent {
  readonly $type: 'AbsenceRejectedEvent';
  absenceId: string;
  employeeId: string;
}
export interface AbsenceCancelledEvent extends BaseEvent {
  readonly $type: 'AbsenceCancelledEvent';
  absenceId: string;
  employeeId: string;
}
export interface AbsenceUpdatedEvent extends BaseEvent {
  readonly $type: 'AbsenceUpdatedEvent';
  absenceId: string;
  employeeId: string;
}
/** requesterId / targetId sind Auth0-subs. */
export interface ShiftSwapRequestedEvent extends BaseEvent {
  readonly $type: 'ShiftSwapRequestedEvent';
  swapId: string;
  requesterId: string;
  targetId: string;
}

export interface ShiftSwapUpdatedEvent extends BaseEvent {
  readonly $type: 'ShiftSwapUpdatedEvent';
  swapId: string;
  /** C#-Enum als Zahl (der Service Bus serialisiert Enums numerisch): 0 PendingPeer, 1 PendingChef, 2 Approved, 3 Rejected, 4 Declined, 5 Cancelled. */
  status: number;
  requesterId: string;
  targetId: string;
}

export interface AbsenceDeletedEvent extends BaseEvent {
  readonly $type: 'AbsenceDeletedEvent';
  absenceId: string;
  employeeId: string;
}

/** employeeId ist die Auth0-sub der antragstellenden Person. */
export interface TimeCorrectionRequestedEvent extends BaseEvent {
  readonly $type: 'TimeCorrectionRequestedEvent';
  requestId: string;
  employeeId: string;
}

export interface TimeCorrectionUpdatedEvent extends BaseEvent {
  readonly $type: 'TimeCorrectionUpdatedEvent';
  requestId: string;
  /** C#-Enum als Zahl: 0 Pending, 1 Approved, 2 Rejected, 3 Cancelled. */
  status: number;
  employeeId: string;
}

// ─── Command failure ─────────────────────────────────────────────────────────

/**
 * Chroniq.Shared.Events.Commands.CommandFailedEvent: an accepted command (202) could not be carried out.
 * The server sends it to the issuing user only. `entityId` is the id of the row the command was about
 * (time entry, absence, shift, template, swap, correction request), the one a page locked while waiting.
 */
export interface CommandFailedEvent extends BaseEvent {
  readonly $type: 'CommandFailedEvent';
  /** Name of the C# CommandType, e.g. 'DeleteTimeEntry'. */
  commandType: string;
  entityId?: string | null;
  /** 'not_found' | 'conflict' | 'month_closed' | 'forbidden' | 'invalid' | 'error'. */
  reason: string;
  correlationId: string;
}

// ─── Union type for exhaustive routing ───────────────────────────────────────

export type AppEvent =
  | TimeEntryStartedEvent
  | TimeEntryStoppedEvent
  | BreakStartedEvent
  | BreakEndedEvent
  | TimeEntryCorrectedEvent
  | TimeEntryCreatedEvent
  | TimeEntryDeletedEvent
  | EmployeeUpdatedEvent
  | EmployeeAnonymisedEvent
  | ShiftCreatedEvent
  | ShiftUpdatedEvent
  | ShiftDeletedEvent
  | ShiftTemplateCreatedEvent
  | ShiftTemplateUpdatedEvent
  | ShiftTemplateDeletedEvent
  | AbsenceRequestedEvent
  | AbsenceApprovedEvent
  | AbsenceRejectedEvent
  | AbsenceCancelledEvent
  | AbsenceUpdatedEvent
  | AbsenceDeletedEvent
  | ShiftSwapRequestedEvent
  | ShiftSwapUpdatedEvent
  | TimeCorrectionRequestedEvent
  | TimeCorrectionUpdatedEvent
  | CommandFailedEvent;

// ─── Discriminator → concrete type lookup ────────────────────────────────────
// Used by EventToken and EventBusService.on() to resolve string literals
// back to the correct concrete interface at compile time.

export interface EventTypeMap {
  TimeEntryStartedEvent: TimeEntryStartedEvent;
  TimeEntryStoppedEvent: TimeEntryStoppedEvent;
  BreakStartedEvent: BreakStartedEvent;
  BreakEndedEvent: BreakEndedEvent;
  TimeEntryCorrectedEvent: TimeEntryCorrectedEvent;
  TimeEntryCreatedEvent: TimeEntryCreatedEvent;
  TimeEntryDeletedEvent: TimeEntryDeletedEvent;
  EmployeeUpdatedEvent: EmployeeUpdatedEvent;
  EmployeeAnonymisedEvent: EmployeeAnonymisedEvent;
  ShiftCreatedEvent: ShiftCreatedEvent;
  ShiftUpdatedEvent: ShiftUpdatedEvent;
  ShiftDeletedEvent: ShiftDeletedEvent;
  ShiftTemplateCreatedEvent: ShiftTemplateCreatedEvent;
  ShiftTemplateUpdatedEvent: ShiftTemplateUpdatedEvent;
  ShiftTemplateDeletedEvent: ShiftTemplateDeletedEvent;
  AbsenceRequestedEvent: AbsenceRequestedEvent;
  AbsenceApprovedEvent: AbsenceApprovedEvent;
  AbsenceRejectedEvent: AbsenceRejectedEvent;
  AbsenceCancelledEvent: AbsenceCancelledEvent;
  AbsenceUpdatedEvent: AbsenceUpdatedEvent;
  AbsenceDeletedEvent: AbsenceDeletedEvent;
  ShiftSwapRequestedEvent: ShiftSwapRequestedEvent;
  ShiftSwapUpdatedEvent: ShiftSwapUpdatedEvent;
  TimeCorrectionRequestedEvent: TimeCorrectionRequestedEvent;
  TimeCorrectionUpdatedEvent: TimeCorrectionUpdatedEvent;
  CommandFailedEvent: CommandFailedEvent;
}

// ─── Class tokens (pass to EventBusService.on()) ─────────────────────────────
// Each token is decorated with @className so the discriminator name is
// available at runtime. The `as const` literal lets TypeScript infer the
// correct union member from the AppEvent union.

@className('TimeEntryStartedEvent')
export class TimeEntryStartedEventToken {
  static readonly $type = 'TimeEntryStartedEvent' as const;
}

@className('TimeEntryStoppedEvent')
export class TimeEntryStoppedEventToken {
  static readonly $type = 'TimeEntryStoppedEvent' as const;
}

@className('BreakStartedEvent')
export class BreakStartedEventToken {
  static readonly $type = 'BreakStartedEvent' as const;
}

@className('BreakEndedEvent')
export class BreakEndedEventToken {
  static readonly $type = 'BreakEndedEvent' as const;
}

@className('TimeEntryCorrectedEvent')
export class TimeEntryCorrectedEventToken {
  static readonly $type = 'TimeEntryCorrectedEvent' as const;
}

@className('TimeEntryCreatedEvent')
export class TimeEntryCreatedEventToken {
  static readonly $type = 'TimeEntryCreatedEvent' as const;
}

@className('TimeEntryDeletedEvent')
export class TimeEntryDeletedEventToken {
  static readonly $type = 'TimeEntryDeletedEvent' as const;
}

@className('EmployeeUpdatedEvent')
export class EmployeeUpdatedEventToken {
  static readonly $type = 'EmployeeUpdatedEvent' as const;
}

@className('EmployeeAnonymisedEvent')
export class EmployeeAnonymisedEventToken {
  static readonly $type = 'EmployeeAnonymisedEvent' as const;
}

@className('ShiftCreatedEvent')
export class ShiftCreatedEventToken {
  static readonly $type = 'ShiftCreatedEvent' as const;
}

@className('ShiftUpdatedEvent')
export class ShiftUpdatedEventToken {
  static readonly $type = 'ShiftUpdatedEvent' as const;
}

@className('ShiftDeletedEvent')
export class ShiftDeletedEventToken {
  static readonly $type = 'ShiftDeletedEvent' as const;
}

@className('ShiftTemplateCreatedEvent')
export class ShiftTemplateCreatedEventToken {
  static readonly $type = 'ShiftTemplateCreatedEvent' as const;
}

@className('ShiftTemplateUpdatedEvent')
export class ShiftTemplateUpdatedEventToken {
  static readonly $type = 'ShiftTemplateUpdatedEvent' as const;
}

@className('ShiftTemplateDeletedEvent')
export class ShiftTemplateDeletedEventToken {
  static readonly $type = 'ShiftTemplateDeletedEvent' as const;
}

@className('AbsenceRequestedEvent')
export class AbsenceRequestedEventToken {
  static readonly $type = 'AbsenceRequestedEvent' as const;
}

@className('AbsenceApprovedEvent')
export class AbsenceApprovedEventToken {
  static readonly $type = 'AbsenceApprovedEvent' as const;
}

@className('AbsenceRejectedEvent')
export class AbsenceRejectedEventToken {
  static readonly $type = 'AbsenceRejectedEvent' as const;
}

@className('AbsenceCancelledEvent')
export class AbsenceCancelledEventToken {
  static readonly $type = 'AbsenceCancelledEvent' as const;
}

@className('AbsenceUpdatedEvent')
export class AbsenceUpdatedEventToken {
  static readonly $type = 'AbsenceUpdatedEvent' as const;
}

@className('AbsenceDeletedEvent')
export class AbsenceDeletedEventToken {
  static readonly $type = 'AbsenceDeletedEvent' as const;
}

@className('ShiftSwapRequestedEvent')
export class ShiftSwapRequestedEventToken {
  static readonly $type = 'ShiftSwapRequestedEvent' as const;
}

@className('ShiftSwapUpdatedEvent')
export class ShiftSwapUpdatedEventToken {
  static readonly $type = 'ShiftSwapUpdatedEvent' as const;
}

@className('TimeCorrectionRequestedEvent')
export class TimeCorrectionRequestedEventToken {
  static readonly $type = 'TimeCorrectionRequestedEvent' as const;
}

@className('TimeCorrectionUpdatedEvent')
export class TimeCorrectionUpdatedEventToken {
  static readonly $type = 'TimeCorrectionUpdatedEvent' as const;
}

@className('CommandFailedEvent')
export class CommandFailedEventToken {
  static readonly $type = 'CommandFailedEvent' as const;
}
